class HeroSlider {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('.hero-slider__track');
    this.slides = Array.from(el.querySelectorAll('.hero-slider__slide'));
    this.nav = el.querySelector('.hero-slider__nav');
    this.navItems = Array.from(el.querySelectorAll('.hero-slider__nav-item'));
    this.prevBtn = el.querySelector('[data-slide-prev]');
    this.nextBtn = el.querySelector('[data-slide-next]');
    this.current = 0;
    this.style = el.dataset.transitionStyle === 'slide' ? 'slide' : 'fade';
    this.autoplay = el.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(el.dataset.autoplaySpeed, 10) || 6000;
    this.transitionSpeed = parseInt(el.dataset.transitionSpeed, 10) || 700;
    this.timer = null;

    // Drag state
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragDeltaX = 0;
    this.viewportWidth = 0;

    el.style.setProperty('--hs-transition', this.transitionSpeed + 'ms');

    this.navItems.forEach((item, i) => {
      item.addEventListener('click', () => this.goTo(i, true));
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goTo(this.current - 1, true));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.goTo(this.current + 1, true));
    }

    el.addEventListener('mouseenter', () => this.stopAutoplay());
    el.addEventListener('mouseleave', () => {
      if (!this.isDragging) this.startAutoplay();
    });

    if (this.style === 'slide') {
      this.setupDrag();
    }

    this.goTo(0, false);
    this.startAutoplay();
  }

  setupDrag() {
    const viewport = this.el.querySelector('.hero-slider__viewport');

    const onDown = (clientX) => {
      this.isDragging = true;
      this.dragStartX = clientX;
      this.dragDeltaX = 0;
      this.viewportWidth = viewport.getBoundingClientRect().width;
      this.track.classList.add('is-dragging');
      this.stopAutoplay();
    };

    const onMove = (clientX) => {
      if (!this.isDragging) return;
      this.dragDeltaX = clientX - this.dragStartX;
      const basePercent = -this.current * 100;
      const dragPercent = (this.dragDeltaX / this.viewportWidth) * 100;
      this.track.style.transform = `translateX(${basePercent + dragPercent}%)`;
    };

    const onUp = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.classList.remove('is-dragging');

      const threshold = this.viewportWidth * 0.15;
      if (this.dragDeltaX <= -threshold) {
        this.goTo(this.current + 1, true);
      } else if (this.dragDeltaX >= threshold) {
        this.goTo(this.current - 1, true);
      } else {
        this.goTo(this.current, false);
      }
      this.dragDeltaX = 0;
      this.startAutoplay();
    };

    // Mouse
    viewport.addEventListener('mousedown', (e) => {
      e.preventDefault();
      onDown(e.clientX);
    });
    window.addEventListener('mousemove', (e) => onMove(e.clientX));
    window.addEventListener('mouseup', onUp);

    // Touch
    viewport.addEventListener('touchstart', (e) => onDown(e.touches[0].clientX), { passive: true });
    viewport.addEventListener('touchmove', (e) => onMove(e.touches[0].clientX), { passive: true });
    viewport.addEventListener('touchend', onUp);
  }

  goTo(index, userInitiated) {
    const total = this.slides.length;
    if (total === 0) return;
    const next = (index + total) % total;

    if (this.style === 'slide') {
      this.track.style.transform = `translateX(${-next * 100}%)`;
    } else {
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === next));
    }

    this.navItems.forEach((item, i) => {
      item.classList.toggle('is-active', i === next);
      item.setAttribute('aria-selected', i === next ? 'true' : 'false');
    });

    this.current = next;

    if (userInitiated) {
      this.stopAutoplay();
      this.startAutoplay();
    }

    this.scrollActiveIntoView();
    this.animateActiveBar();
  }

  scrollActiveIntoView() {
    const activeItem = this.navItems[this.current];
    if (!activeItem || !this.nav) return;

    activeItem.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }

  animateActiveBar() {
    // Reset every item's bar instantly (no transition) so switching slides
    // never shows a stray bar mid-animation on the item you just left.
    this.navItems.forEach((item) => {
      const f = item.querySelector('.hero-slider__nav-bar-fill');
      if (f) {
        f.style.transition = 'none';
        f.style.width = '0%';
      }
    });

    const activeItem = this.navItems[this.current];
    if (!activeItem) return;
    const fill = activeItem.querySelector('.hero-slider__nav-bar-fill');
    if (!fill) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.autoplay) {
          // Autoplay on: fill ticks up over the autoplay duration, showing
          // how long until the next slide.
          fill.style.transition = `width ${this.autoplaySpeed}ms linear`;
          fill.style.width = '100%';
        } else {
          // Autoplay off: no timed duration to show, so just fill in
          // quickly to mark the active slide instead of staying at 0%.
          fill.style.transition = `width ${this.transitionSpeed}ms ease`;
          fill.style.width = '100%';
        }
      });
    });
  }

  startAutoplay() {
    if (!this.autoplay || this.slides.length < 2) return;
    this.stopAutoplay();
    this.timer = setInterval(() => this.goTo(this.current + 1, false), this.autoplaySpeed);
    this.animateActiveBar();
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

document.querySelectorAll('[data-hero-slider]').forEach((el) => new HeroSlider(el));
